import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import dayjs from 'dayjs'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import {SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA, SecondaryTherapyDataSchemaElement} from 'src/constants/secondaryTherapyFields'
import UnitValueField from 'src/pages-new/therapies/dialog/UnitValueField'
import {
  DEFAULT_DOSAGE_UNIT_TYPE,
  DOSAGE_UNIT_OPTIONS,
  MODE_OF_ASSUMPTION_OPTIONS
} from 'src/pages-new/patient/tabs/therapy/therapyOptions'
import apiClient from 'src/services/apiClient'
import {
  AsNeededDoseForm,
  combineDateAndTime,
  getBodyPartSuggestions,
  SecondaryAssumption,
  validateAsNeededDose
} from 'src/constants/secondaryAssumptions'
import BodyPartPickerNew from './BodyPartPickerNew'
import SectionTitleNew from './SectionTitleNew'

// The "Assunzione al bisogno" dialog of the diary (therapy-service
// specs/2026-10-as-needed-assumptions.md, RF4): the patient records a dose of any drug of the
// catalog, already taken — psp-backend hangs it on their "al bisogno" therapy for that drug
// (updating it with notes, dosage and route), creating it when there is none.

interface AsNeededAssumptionDialogNewProps {
  open: boolean
  onClose: () => void
  onCreated: (created: SecondaryAssumption) => void
}

const emptyForm = (): AsNeededDoseForm => ({
  drugId: '',
  date: dayjs().startOf('day').toISOString(),
  time: dayjs().startOf('minute').toISOString(),
  bodyPart: [],
  notes: '',
  assumptionDosage: {unitType: DEFAULT_DOSAGE_UNIT_TYPE},
  modeOfAssumption: ''
})

export default function AsNeededAssumptionDialogNew({open, onClose, onCreated}: AsNeededAssumptionDialogNewProps): JSX.Element {
  const toast = useToast()
  const configuredBodyParts = useAppSelector(
    state => state.pspConfigReducer.data.therapyDiaryConfigurations?.secondaryTherapiesConfiguration?.bodyParts
  )
  const bodyPartSuggestions = getBodyPartSuggestions(configuredBodyParts)
  // same labels as the accessory therapy dialog (SecondaryTherapyDialogNew), as the PSP named them
  const configuredDataSchema: SecondaryTherapyDataSchemaElement[] | undefined = useAppSelector(
    (state) => state.pspConfigReducer.data.therapyConfigurations?.secondaryTherapiesConfiguration?.dataSchema
  )
  const dataSchema = configuredDataSchema ?? SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA
  const fieldLabel = (key: string, fallback: string): string => dataSchema.find((element) => element.value === key)?.name ?? fallback

  const [form, setForm] = useState<AsNeededDoseForm>(emptyForm)
  const [drugOptions, setDrugOptions] = useState<{value: string; label: string}[]>([])
  const [statusDrugs, setStatusDrugs] = useState<string>()
  const [error, setError] = useState<string>()
  const [status, setStatus] = useState<string>()

  // Reset on every open: taken today, "adesso". Same drug list as the accessory therapy dialog
  // (SecondaryTherapyDialogNew): the whole catalog, alphabetical.
  useEffect(() => {
    if (!open) { return }
    setForm(emptyForm())
    setError(undefined)
    setStatus(undefined)
    apiClient.getDrugs('', setStatusDrugs)
      .then((drugs: Record<string, any>[] = []) => {
        const sorted = [...drugs].sort((a, b) => a.name.localeCompare(b.name, 'it'))
        setDrugOptions(sorted.map((drug) => ({value: drug._id, label: drug.name})))
      })
      .catch(() => {})
  }, [open])

  const isPending = status === STATUS.PENDING
  const update = (patch: Partial<AsNeededDoseForm>): void => {
    setForm((prev) => ({...prev, ...patch}))
    setError(undefined)
  }

  const onSubmit = async (): Promise<void> => {
    const validationError = validateAsNeededDose(form)
    setError(validationError)
    if (validationError) { return }
    const bodyParts = Array.from(new Set(form.bodyPart.map(part => part.trim()).filter(Boolean)))
    const notes = form.notes.trim()
    const {value: dosageValue, unitType: dosageUnit} = form.assumptionDosage
    try {
      const created = await apiClient.postSecondaryAssumption({
        drugId: form.drugId,
        takenAt: combineDateAndTime(form.date, form.time) as string,
        ...bodyParts.length > 0 ? {bodyPart: bodyParts} : {},
        ...notes ? {notes} : {},
        ...dosageValue !== undefined && dosageUnit ? {assumptionDosage: {value: dosageValue, unitType: dosageUnit}} : {},
        ...form.modeOfAssumption ? {modeOfAssumption: form.modeOfAssumption} : {}
      }, setStatus)
      if (!created) { throw new Error('create failed') }
      toast.success('Assunzione registrata')
      onCreated(created)
    } catch {
      toast.error('Impossibile registrare l\'assunzione. Controlla data e ora e riprova.')
    }
  }

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Assunzione al bisogno"
      maxWidth="sm"
      actions={(
        <Button variant="primary" loading={isPending} disabled={isPending} onClick={onSubmit}>
          Registra assunzione
        </Button>
      )}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        <SelectNew
          label="Farmaco"
          required
          fullWidth
          showSearch
          placeholder={statusDrugs === STATUS.PENDING ? 'Caricamento…' : 'Cerca un farmaco'}
          disabled={isPending}
          error={error === 'Scegli il farmaco'}
          value={form.drugId}
          onChange={(drugId) => update({drugId})}
          options={drugOptions}
        />

        <Box>
          <SectionTitleNew icon={EventAvailableOutlinedIcon}>Quando l'hai assunto?</SectionTitleNew>
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
            <DatePickerNew
              label="Data"
              required
              fullWidth
              disabled={isPending}
              error={Boolean(error) && error !== 'Scegli il farmaco'}
              maxDate={dayjs().toISOString()}
              value={form.date}
              onChange={(date) => update({date})}
            />
            <TimePickerNew
              label="Ora"
              fullWidth
              disabled={isPending}
              error={Boolean(error) && error !== 'Scegli il farmaco'}
              showNowAction
              value={form.time}
              onChange={(time) => update({time})}
            />
          </Box>
        </Box>

        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
          <UnitValueField
            label={fieldLabel('assumptionDosage', 'Dosaggio per assunzione')}
            value={form.assumptionDosage}
            onChange={(assumptionDosage) => update({assumptionDosage})}
            unitOptions={DOSAGE_UNIT_OPTIONS}
            disabled={isPending}
          />
          <SelectNew
            label={fieldLabel('modeOfAssumption', 'Via di assunzione')}
            fullWidth
            disabled={isPending}
            value={form.modeOfAssumption}
            onChange={(modeOfAssumption) => update({modeOfAssumption})}
            options={MODE_OF_ASSUMPTION_OPTIONS}
            placeholder="Seleziona una opzione"
          />
        </Box>

        <BodyPartPickerNew
          value={form.bodyPart}
          onChange={(bodyPart) => update({bodyPart})}
          suggestions={bodyPartSuggestions}
          disabled={isPending}
        />

        <TextAreaNew
          label="Note"
          fullWidth
          rows={3}
          disabled={isPending}
          value={form.notes}
          onChange={(notes) => update({notes})}
        />

        {error && (
          <Typography variant="caption" sx={{color: 'error.main'}}>{error}</Typography>
        )}
      </Box>
    </AppDialog>
  )
}
